/**
 * @module TextAreaEditor
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {TextAreaEditor} from '@ey/canvascoreservices';
import {labels} from '../../../util/utils';
import Utility from '../../../util/utilityfunctions';
import {useIsAresView} from '../../../util/Ares/customHooks';

export default function TextAreaEditorWrapper(props) {
	const {
		charactersLabel = labels.characters,
		isDisabled,
		maxLength,
		ofLabel = labels.of,
		onChange,
		onFocus,
		onKeyDown,
		placeholder,
		showCounter,
		textAreaError,
		textAreaErrorMessage,
		value,
		focusOnRender,
		onBlur,
		textAreaEditorLabel,
		className,

		//These are required only if its expandable
		expandable,
		maxExpandTo,
		initialHeight,
		isAresOutlineErrorIcon,
		labelPosition
	} = props;

	//This is because scrollHeight is returning in PX
	const basePx = 16;

	const isAresView = useIsAresView();

	const handleOnFocus = (e) => {
		onFocus && onFocus(e);
	};

	const handleOnChange = (e) => {
		handleExpandableHeight(e);
		onChange && onChange(e);
	};

	const handleExpandableHeight = (e) => {
		let textArea = e.target;

		const currentHeight = parseFloat(textArea.scrollHeight / basePx, 10);

		if (e.currentTarget.value === '') {
			textArea.style.height = initialHeight + 'rem';
		} else {
			if (currentHeight < maxExpandTo) {
				let expandedHeight = textArea.scrollHeight / 10;

				if (expandedHeight > maxExpandTo) {
					expandedHeight = maxExpandTo;
				}

				textArea.style.height = expandedHeight + 'rem';
			} else {
				textArea.style.height = maxExpandTo + 'rem';
			}
		}
	};

	return (
		<StyledTextAreaEditor
			className={`StyledTextAreaEditor text-area-editor ${className || ''} ${isAresView ? 'AresTextAreaEditor' : ''} ${
				textAreaError ? 'ErrorTextAreaEditor' : ''
			}`}
		>
			<TextAreaEditor
				focusOnRender={focusOnRender}
				onBlur={onBlur}
				showCounter={showCounter}
				charactersLabel={charactersLabel}
				maxLength={maxLength}
				ofLabel={ofLabel}
				isDisabled={isDisabled}
				onChange={expandable ? handleOnChange : onChange}
				placeholder={placeholder}
				textAreaError={textAreaError}
				textAreaErrorMessage={textAreaErrorMessage}
				value={value}
				textAreaEditorLabel={textAreaEditorLabel}
				theme={Utility.getTheme()}
				onFocus={handleOnFocus}
				onKeyDown={onKeyDown}
				isAresOutlineErrorIcon={isAresOutlineErrorIcon}
				labelPosition={labelPosition}
			/>
		</StyledTextAreaEditor>
	);
}

TextAreaEditor.propTypes = {
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	placeholder: PropTypes.string,
	maxLength: PropTypes.number.isRequired,
	isDisabled: PropTypes.bool,
	textAreaError: PropTypes.bool,
	textAreaErrorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	textAreaEditorLabel: PropTypes.string,
	expandable: PropTypes.bool
};

TextAreaEditor.defaultProps = {
	expandable: false,
	maxExpandTo: 10, //Rem
	showCounter: true
};

const StyledTextAreaEditor = styled.section`
	display: flex;
	flex-direction: column;
	&.AresTextAreaEditor {
		&.ErrorTextAreaEditor {
			.motif-form-field-text-input {
				&.motif-form-field {
					border: var(--px-1) solid var(--red-600) !important;
				}
			}
		}
		.pull-right-counter {
			font-weight: 300 !important;
			font-size: var(--px-12) !important;
			line-height: var(--px-20) !important;
			color: var(--neutrals-900) !important;
		}
		.motif-form-field-text-input {
			&.motif-form-field {
				width: 100%;
				height: calc(100% - var(--px-30)) !important;
				.motif-input-component {
					margin-top: var(--px-25) !important;
					width: calc(100% - var(--px-70)) !important;
					.motif-text-area {
						height: auto !important;
					}
				}
			}
		}
	}
	.pull-right-counter {
		margin: 0;
		text-align: right;
		font-weight: 300;
		font-size: var(--px-12);
		line-height: var(--px-20);
		color: var(--neutrals-900);
	}
	.text-area-editor {
		.motif-form-field {
			.motif-input-component {
				.motif-error-icon {
					width: var(--px-20) !important;
					height: var(--px-20) !important;
					display: inline-flex;
					align-items: center;
				}
			}
		}
	}
	.motif-form-field-text-input {
		.motif-label {
			width: calc(100% - var(--px-10)) !important;
		}
		.motif-input-component {
			.motif-text-area {
				resize: none;
				margin-bottom: var(--px-10);
			}
			.motif-error-icon {
				width: var(--px-20) !important;
				height: var(--px-20) !important;
				display: inline-flex;
				align-items: center;
			}
		}
		.motif-error-message.motif-error-text-input {
			margin-left: var(--px-10) !important;
			margin-top: var(--px-5) !important;
		}
		.motif-error-message {
			display: flex;
			align-items: flex-start;
			flex-direction: row;
			width: 100%;
			margin-top: var(--px-5);
			&.motif-error-text-input {
				margin-left: var(--px-10) !important;
				margin-top: var(--px-5) !important;
			}
			.motif-error-icon {
				width: var(--px-16) !important;
				height: var(--px-16) !important;
			}
		}
	}
`;
